import ISupplierModel from "@models/ISupplierModel";
import InventoryOperationEnum from "@models/InventoryOperationEnum";
import { FilterValue } from "@models/IFilterPresets";
import { FilterValues, IFilterField, IFilterSection, IFilterSelectOption } from "@models/IFilterConfig";

export interface IInventoryLogFilterContext {
    supplierModel: ISupplierModel;
    /** The supplier's warehouses as select options (value = warehouse guid); the filter shows only with warehouses */
    warehouses: IFilterSelectOption[];
    formatMessage: (id: string) => string;
}

/** Baseline inventory log filters: none, so "All entries" is the item's whole log. */
export const INVENTORY_LOG_DEFAULT_FILTERS: FilterValues = {};

/**
 * URL params that narrow the log to one order or PO but are not FilterBar fields: the "View inventory log" links on
 * order and PO lines set them, the page shows them as a banner, and the FilterBar keeps them when it rewrites the URL.
 */
export const INVENTORY_LOG_SCOPE_PARAMS = ["order_guid", "po_guid"];

/** The query the inventory log FilterBar emits: keys are the `GET /v1/inventory/:catalogItemGuid/logs` params. */
export interface IInventoryLogFilterQuery {
    order?: unknown;
    po?: unknown;
    note?: unknown;
    operation?: unknown;
    warehouse_guid?: unknown;
    date_from?: unknown;
    date_to?: unknown;
}

/** Inventory log operation labels (translation ids), shared by the filter and the table. */
export const INVENTORY_LOG_OPERATION_LABELS: { [key in InventoryOperationEnum]: string } = {
    [InventoryOperationEnum.ADD]: "add",
    [InventoryOperationEnum.SUBTRACT]: "subtract",
    [InventoryOperationEnum.CYCLE_COUNT]: "cycle_count",
};

const joinComma = (value: FilterValue): unknown => (Array.isArray(value) ? value.join(",") : value);

/**
 * Compile the inventory log filter config. Field keys ARE the API's query param names, so presets store what the
 * page sends. Order and PO match by name (contains); the exact order / PO scope comes from line links instead.
 */
export function buildInventoryLogFilterSections(ctx: IInventoryLogFilterContext): IFilterSection[] {
    const { supplierModel, warehouses, formatMessage } = ctx;

    const main: IFilterField[] = [
        { key: "order", label: "order", type: "text", apiKey: "order" },
        { key: "po", label: "purchase_order", type: "text", apiKey: "po" },
        { key: "note", label: "note", type: "text", apiKey: "note" },
        {
            key: "operation",
            label: "operation",
            type: "multiselect",
            apiKey: "operation",
            options: (Object.keys(INVENTORY_LOG_OPERATION_LABELS) as unknown as InventoryOperationEnum[]).map((id) => ({
                label: formatMessage(INVENTORY_LOG_OPERATION_LABELS[id]),
                value: String(id),
            })),
            serialize: joinComma,
        },
    ];

    if (supplierModel.has_warehouses && warehouses.length) {
        main.push({
            key: "warehouse_guid",
            label: "warehouse",
            type: "select",
            apiKey: "warehouse_guid",
            options: [{ label: formatMessage("all"), value: "" }, ...warehouses],
        });
    }

    // Calendar days in the user's timezone; either bound works on its own.
    const dates: IFilterField[] = [
        { key: "date_from", label: "start_date", type: "date", apiKey: "date_from" },
        { key: "date_to", label: "end_date", type: "date", apiKey: "date_to" },
    ];

    return [
        { id: "main", title: "search", filters: main },
        { id: "dates", title: "date_range", filters: dates },
    ];
}
